import * as yup from 'yup';

import { getTranslatedMessage } from '@/i18n/utils';

import { IndiciaReasonCode } from '@/types/api/tax-info';

import { ForeignIndiciaCountryDetail } from '@/utils/user';

import {
  FOREIGN_INDICIA_REASON_GROUPS,
  FOREIGN_INDICIA_REASON_OTHER_MAX_LENGTH,
} from './constants';

const FOREIGN_INDICIA_REASON_OPTION_CODES = [
  ...new Set([
    ...FOREIGN_INDICIA_REASON_GROUPS.flatMap(group => group.optionCodes),
    IndiciaReasonCode.OTHER,
  ]),
] as IndiciaReasonCode[];

function isForeignIndiciaReasonCode(code?: string): code is IndiciaReasonCode {
  return (
    !!code &&
    FOREIGN_INDICIA_REASON_OPTION_CODES.includes(code as IndiciaReasonCode)
  );
}

export function buildIndiciaReasonCodeValidator(
  foreignIndiciaCountries: ForeignIndiciaCountryDetail[],
) {
  const countryCodes = foreignIndiciaCountries.map(
    ({ countryCode }) => countryCode,
  );

  return yup
    .object()
    .default({})
    .when(['hasForeignIndicia', 'isForeignTaxResident'], {
      is: (hasForeignIndicia: boolean, isForeignTaxResident: boolean) =>
        hasForeignIndicia === false && isForeignTaxResident === false,
      then: schema =>
        schema.test(
          'foreign-indicia-country-reasons',
          getTranslatedMessage(
            'taxInfoDrawer.question2.foreignIndicia.required',
          ),
          value => {
            const selections = (value ?? {}) as Record<string, string>;

            return countryCodes.every(countryCode =>
              isForeignIndiciaReasonCode(selections[countryCode]),
            );
          },
        ),
      otherwise: schema => schema.notRequired(),
    });
}

export function buildIndiciaReasonOtherValidator(
  foreignIndiciaCountries: ForeignIndiciaCountryDetail[],
) {
  const countryCodes = foreignIndiciaCountries.map(
    ({ countryCode }) => countryCode,
  );

  return yup
    .object()
    .default({})
    .when(['hasForeignIndicia', 'isForeignTaxResident', 'indiciaReasonCode'], {
      is: (hasForeignIndicia: boolean, isForeignTaxResident: boolean) =>
        hasForeignIndicia === false && isForeignTaxResident === false,
      then: schema =>
        schema.test(
          'foreign-indicia-other-reasons',
          getTranslatedMessage(
            'taxInfoDrawer.question2.foreignIndicia.reasonOther.required',
          ),
          function validateForeignIndiciaOtherReasons(value) {
            const selections = this.parent.indiciaReasonCode as
              | Record<string, string>
              | undefined;
            const otherReasons = (value ?? {}) as Record<string, string>;

            return countryCodes.every(countryCode => {
              if (selections?.[countryCode] !== IndiciaReasonCode.OTHER) {
                return true;
              }

              const otherText = otherReasons[countryCode]?.trim();

              return (
                !!otherText &&
                otherText.length <= FOREIGN_INDICIA_REASON_OTHER_MAX_LENGTH
              );
            });
          },
        ),
      otherwise: schema => schema.notRequired(),
    });
}
